import { SubscriptionInterval } from 'constants/order';
import { useAuth } from 'contexts/auth';
import { Address } from 'interfaces/address';
import { EffectiveCart } from 'interfaces/cart';
import { PdProductItem } from 'interfaces/line-item';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import useSWR, { KeyedMutator } from 'swr';
import {
  convertOneOffPurchaseItemToOneOffPurchaseInput,
  getSubscriptionIntervalByDaysCount,
} from 'utils/subscription';
import * as subscriptionsApi from '../../../../api/subscriptions';
import { SubscriptionStatus } from '../../../../constants/subscription';
import { useAddresses } from '../../../../contexts/addresses';
import {
  OneOffPurchaseItem,
  SubscriptionDetails,
  SubscriptionDetailsWithEffectiveCart,
  UpdateSubscriptionDetailsInput,
} from '../../../../interfaces/subscription';
import { showSuccessToast } from '../../../../utils/toasts';
import { SubscriptionEditSubView } from './constants';

type PossibleError = subscriptionsApi.SubscriptionMovError;

interface Props {
  id: string;
}

export interface SubscriptionDetailsStateOutput {
  shippingAddress: Address | undefined;
  billingAddress: Address | undefined;
  items: {
    quantity: number;
    price: number;
    variantId: number;
    interval: SubscriptionInterval | undefined;
    masterProductId: number;
    masterVariantId: number;
  }[];
  subscription: SubscriptionDetails | undefined;
  oosItemsDetails: PdProductItem[] | undefined;
  areAllDeliverNowItemsOos: boolean | undefined;
  effectiveCart: EffectiveCart | undefined;
  loading: boolean;
  recoverItem: (variantId: number) => Promise<void>;
  updateSubscription: (update: Partial<UpdateSubscriptionDetailsInput>) => Promise<void>;
  itemQuantityChange: ({
    variantId,
    quantity,
  }: {
    variantId: number;
    quantity: number;
  }) => Promise<void>;
  deleteOneOffItem: (variantId: number) => Promise<void>;
  statusChange: ({
    status,
    reason,
    comment,
  }: {
    status: SubscriptionStatus;
    reason: string;
    comment?: string | undefined;
  }) => Promise<void>;
  onGoToMainView: () => void;
  onStatusRequest: (status: SubscriptionStatus) => void;
  activeSubView: SubscriptionEditSubView | null;
  setActiveSubView: Dispatch<SetStateAction<SubscriptionEditSubView | null>>;
  requestedStatus: SubscriptionStatus | null;
  addOneOffItem: ({
    id,
    oneOffProductDetails,
  }: {
    id: string;
    oneOffProductDetails: OneOffPurchaseItem | PdProductItem;
  }) => Promise<void>;
  oneTimeItemQuantityChange: ({
    variantId,
    quantity,
  }: {
    variantId: string;
    quantity: number;
  }) => Promise<void>;
  mutate: KeyedMutator<SubscriptionDetailsWithEffectiveCart>;
  deliverSubOnDemand: () => Promise<void>;
  mutateError: any;
  errorMessage: null | PossibleError;
  resetError: () => void;
}

export const useSubscriptionDetailsState = ({ id }: Props): SubscriptionDetailsStateOutput => {
  const { addresses } = useAddresses();
  const { customer } = useAuth();
  const router = useRouter();

  const [requestedStatus, setRequestedStatus] = useState<SubscriptionStatus | null>(null);
  const [activeSubView, setActiveSubView] = useState<SubscriptionEditSubView | null>(null);
  const [errorMessage, setErrorMessage] = useState<PossibleError | null>(null);
  const resetError = () => {
    setErrorMessage(null);
  };
  // should avoid revalidate if not stale
  const {
    data,
    isValidating,
    mutate,
    error: mutateError,
  } = useSWR<SubscriptionDetailsWithEffectiveCart>(
    () => (id && customer ? `${customer.customerId}-subscription-${id}` : null),
    () => {
      if (!customer) {
        throw new Error('no customer data');
      }
      return subscriptionsApi.fetchSubscriptionDetailsWithEffectiveCart(customer.customerId, id);
    }
  );
  const { effectiveCart: initialEffectiveCart, subscription } = data || {};

  const oosItems = useMemo(() => data?.oos ?? [], [data?.oos]);

  const oosItemsDetails = useMemo(() => {
    if (!subscription || !initialEffectiveCart) return;
    const items = initialEffectiveCart.items.filter((cartItem) => {
      const cartItems = subscription.items.find((item) => item.variantId === cartItem.variantId);
      return cartItems;
    });

    return items.filter((item) =>
      oosItems.find((oosItem) => oosItem.isOutOfStock && oosItem.variantId === item.variantId)
    );
  }, [subscription, initialEffectiveCart, oosItems]);

  const areAllDeliverNowItemsOos = useMemo(() => {
    if (!subscription) return;
    const hasAllItems = subscription.items.every((item) =>
      oosItems.find((oosItem) => oosItem.isOutOfStock && oosItem.variantId === item.variantId)
    );
    return hasAllItems;
  }, [subscription, oosItems]);

  const subscriptionItemsPayload = useMemo(() => {
    if (!initialEffectiveCart) {
      return undefined;
    }

    if (subscription) {
      return {
        items: subscription.items.map((item) => ({
          price: item.price * 100,
          variantId: item.variantId,
          quantity: item.quantity,
          masterProductId: item.masterProductId,
          masterVariantId: item.masterVariantId,
          interval: getSubscriptionIntervalByDaysCount(subscription.intervalInDays), //item.customAttributes && item.customAttributes.interval
        })),
      };
    }
  }, [initialEffectiveCart, subscription]);

  const subscriptionItems = useMemo(() => {
    return (subscriptionItemsPayload?.items || []).map((effectiveCartItem) => ({
      ...effectiveCartItem,
      quantity: effectiveCartItem.quantity || 0,
    }));
  }, [subscriptionItemsPayload]);

  const shippingAddress = useMemo(() => {
    if (data?.subscription) {
      return data.subscription && addresses
        ? addresses.find((address) => address.id === data.subscription.shippingAddressId)
        : undefined;
    }
  }, [data, addresses]);

  const billingAddress = useMemo(() => {
    if (data?.subscription) {
      return data.subscription && addresses && data.subscription.billingAddressId
        ? addresses.find((address) => address.id === data.subscription.billingAddressId)
        : undefined;
    }
  }, [data, addresses]);

  const updateSubscription = useCallback(
    async (update: Partial<UpdateSubscriptionDetailsInput>) => {
      if (data) {
        try {
          await subscriptionsApi.updateSubscription(data.subscription.id, update);
        } catch (error) {
          if (subscriptionsApi.isSubscriptionMovError(error)) {
            setErrorMessage(error);
          }
          throw error;
        } finally {
          mutate();
        }
      }
    },
    [data, mutate]
  );

  const updateOneOffItem = useCallback(
    async (oneOffProductDetails: OneOffPurchaseItem, variantId: number) => {
      try {
        if (data) {
          await subscriptionsApi.updateOneOffPurchase(
            data.subscription.id,
            variantId,
            convertOneOffPurchaseItemToOneOffPurchaseInput(oneOffProductDetails)
          );
          showSuccessToast({
            message: 'account:subscription:items-quantity-change:success-message',
          });
          mutate();
        }
      } catch (error) {
        console.log(error);
      }
    },
    [data, mutate]
  );

  const deleteOneOffItem = useCallback(
    async (variantId: number) => {
      try {
        if (data) {
          await subscriptionsApi.deleteOneOffPurchase(data.subscription.id, variantId);
          showSuccessToast({
            message: 'account:subscription:items-quantity-change:success-message',
          });
          mutate();
        }
      } catch (error) {
        console.log(error);
      }
    },
    [data, mutate]
  );

  const addOneOffItem: SubscriptionDetailsStateOutput['addOneOffItem'] = useCallback(
    async ({ id, oneOffProductDetails }) => {
      try {
        await subscriptionsApi.addOneOffItemToSubscription(
          id,
          convertOneOffPurchaseItemToOneOffPurchaseInput(oneOffProductDetails)
        );
        mutate();
      } catch (error) {
        console.log(error);
      }
    },
    [mutate]
  );

  const markSubscriptionItemAsInactive = useCallback(
    async (variantId: number) => {
      if (!subscription) {
        throw new Error();
      }
      try {
        await subscriptionsApi.markProductAsInactive(subscription.id, variantId);
      } catch (error) {
        if (subscriptionsApi.isSubscriptionMovError(error)) {
          setErrorMessage(error);
        }
        throw error;
      } finally {
        mutate();
      }
    },
    [mutate, subscription]
  );

  const recoverItem = useCallback(
    async (variantId: number) => {
      try {
        if (!subscription) {
          throw new Error();
        }
        await subscriptionsApi.recoverSubscriptionItem(subscription.id, variantId);
        showSuccessToast({
          message: 'account:subscription:item-recover:success-message',
        });
        mutate();
      } catch (error) {
        console.log(error);
      }
    },
    [subscription, mutate]
  );

  const itemQuantityChange: SubscriptionDetailsStateOutput['itemQuantityChange'] = useCallback(
    async ({ variantId, quantity }) => {
      try {
        if (!subscription) {
          throw new Error('wrong usage of itemQuantityChange');
        }
        if (quantity === 0) {
          await markSubscriptionItemAsInactive(variantId);
        } else {
          const items = subscription.items
            .map((item) => {
              if (item.masterVariantId === variantId) {
                return { ...item, quantity };
              }
              return item;
            })
            .filter((item) => item.quantity !== 0);
          await updateSubscription({ items });
        }
        showSuccessToast({
          message: 'account:subscription:items-quantity-change:success-message',
        });
      } catch (error) {
        console.error(error);
      }
    },
    [subscription, updateSubscription, markSubscriptionItemAsInactive]
  );

  const oneTimeItemQuantityChange: SubscriptionDetailsStateOutput['oneTimeItemQuantityChange'] =
    useCallback(
      ({ variantId, quantity }) => {
        if (!subscription || !subscription.oneOffPurchaseItems) {
          throw new Error('wrong usage of oneTimeItemQuantityChange');
        }
        if (quantity === 0) {
          return deleteOneOffItem(Number(variantId));
        } else {
          const item = subscription.oneOffPurchaseItems.find(
            (item) => item.variantId === variantId
          );
          if (!item) {
            throw new Error(`cant find oneOffPurchaseItem with ${variantId}`);
          }
          const _item = {
            ...item,
            quantity,
          };
          return updateOneOffItem(_item, Number(variantId));
        }
      },
      [deleteOneOffItem, subscription, updateOneOffItem]
    );

  const deliverSubOnDemand: SubscriptionDetailsStateOutput['deliverSubOnDemand'] =
    useCallback(async () => {
      if (!subscription) {
        throw new Error('wrong usage of deliverSubOnDemand');
      }
      await subscriptionsApi.deliverSubOnDemand({
        subscriptionId: subscription.id,
      });
    }, [subscription]);

  const statusChange = useCallback(
    async ({
      status,
      reason,
      comment,
    }: {
      status: SubscriptionStatus;
      reason: string;
      comment?: string;
    }) => {
      let payload;
      switch (status) {
        case SubscriptionStatus.CANCELLED:
          payload = {
            cancel: true,
            cancelReason: reason,
            ...(comment && { canceledReasonText: comment }),
          };
          break;
        case SubscriptionStatus.PAUSED:
          payload = {
            paused: true,
            pausedReason: reason,
            ...(comment && { pausedReasonText: comment }),
          };
          break;
        default:
          payload = { paused: false, cancel: false };
      }
      await updateSubscription(payload);
      showSuccessToast({
        message: 'account:subscription:state-change:success-message',
      });
      if (status === SubscriptionStatus.CANCELLED) {
        router.push(`/account/subscriptions?status=${status}`);
      }
    },
    [updateSubscription, router]
  );

  const onGoToMainView = useCallback(() => {
    setRequestedStatus(null);
    setActiveSubView(null);
  }, []);

  const onStatusRequest = useCallback((status: SubscriptionStatus) => {
    setRequestedStatus(status);
    setActiveSubView(SubscriptionEditSubView.SUBSCRIPTION_STATUS);
  }, []);

  return {
    activeSubView,
    areAllDeliverNowItemsOos,
    billingAddress,
    effectiveCart: initialEffectiveCart,
    items: subscriptionItems,
    loading: isValidating,
    mutateError,
    oosItemsDetails,
    requestedStatus,
    shippingAddress,
    subscription,
    addOneOffItem,
    deleteOneOffItem,
    deliverSubOnDemand,
    itemQuantityChange,
    mutate,
    oneTimeItemQuantityChange,
    onGoToMainView,
    onStatusRequest,
    recoverItem,
    setActiveSubView,
    statusChange,
    updateSubscription,
    errorMessage,
    resetError,
  };
};
